/**
 * TemplateCustomizer Component
 *
 * The "Customize" wizard step (Step 5 of 6). Lets users configure each selected
 * theme template with:
 *
 *  1. Per-template layout preset selection  (not available for homepage, which uses
 *     the global LayoutPicker from Step 3)
 *  2. Modular section add/remove/reorder/variant via SectionManager
 *
 * Industry-specific sections (mortgage calculator, agent list, listings) are
 * automatically shown or hidden based on the selected industry.
 *
 * @param {string[]}  selectedTemplates     - Array of selected template IDs
 * @param {string|null} industry            - Selected industry slug
 * @param {Object}    customizations        - Current templateCustomizations object keyed by templateId
 * @param {function}  onCustomizationChange - Callback: (templateId, updatedConfig) => void
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Chip,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SectionManager from './SectionManager';

// ---------------------------------------------------------------------------
// Template metadata (display names / icons for the accordion headers)
// ---------------------------------------------------------------------------

const TEMPLATE_META = {
  home:     { name: 'Homepage',          icon: '🏠', note: 'Layout set in Step 3 (Layout Picker)' },
  blog:     { name: 'Blog / Archive',    icon: '📝', note: null },
  single:   { name: 'Single Post',       icon: '📄', note: null },
  about:    { name: 'About',             icon: '👥', note: null },
  services: { name: 'Services',          icon: '🛠️', note: null },
  contact:  { name: 'Contact',           icon: '📬', note: null },
  gallery:  { name: 'Gallery / Portfolio', icon: '🖼️', note: null },
  '404':    { name: '404 Error Page',    icon: '🚧', note: null },
  search:   { name: 'Search Results',    icon: '🔍', note: null }
};

// ---------------------------------------------------------------------------
// Per-template layout presets (mirrors sectionLibrary.js in the backend)
// null entry means the template uses the global homepage layout
// ---------------------------------------------------------------------------

const TEMPLATE_LAYOUT_PRESETS = {
  home: null,
  about: [
    { id: 'full-width-story', name: 'Full-Width Story', icon: '📖', description: 'Narrative layout with full-width sections' },
    { id: 'sidebar-bio',      name: 'Sidebar Bio',      icon: '👤', description: 'Headshot sidebar with bio content' },
    { id: 'team-grid',        name: 'Team Grid',        icon: '👥', description: 'Team member cards in a grid' },
    { id: 'timeline',         name: 'Company Timeline', icon: '📅', description: 'Company history as a visual timeline' }
  ],
  services: [
    { id: 'card-grid',   name: 'Service Cards', icon: '📦', description: 'Services as cards in a responsive grid' },
    { id: 'icon-list',   name: 'Icon + Text',   icon: '📋', description: 'Each service with an icon and description' },
    { id: 'tabs',        name: 'Tabbed',        icon: '🗂️', description: 'Service categories organised in tabs' },
    { id: 'accordion',   name: 'Accordion',     icon: '📂', description: 'Expandable service descriptions' }
  ],
  contact: [
    { id: 'simple-form', name: 'Simple Form',  icon: '📝', description: 'Clean centred contact form' },
    { id: 'map-form',    name: 'Map + Form',   icon: '🗺️', description: 'Map on one side, form on the other' },
    { id: 'two-column',  name: 'Two Column',   icon: '⬛', description: 'Contact info and form side by side' }
  ],
  gallery: [
    { id: 'masonry',       name: 'Masonry',              icon: '🧱', description: 'Pinterest-style variable-height grid' },
    { id: 'uniform-grid',  name: 'Uniform Grid',         icon: '⬜', description: 'Equal-sized image grid' },
    { id: 'slideshow',     name: 'Full-Width Slideshow',  icon: '🎞️', description: 'Large-format image slideshow' }
  ],
  blog: [
    { id: 'standard-list',   name: 'Standard List',       icon: '📰', description: 'Classic blog post list layout' },
    { id: 'card-grid',        name: 'Card Grid',           icon: '🗃️', description: 'Blog post cards in a responsive grid' },
    { id: 'featured-sidebar', name: 'Featured + Sidebar',  icon: '📌', description: 'Featured post with sidebar of others' }
  ],
  single: [
    { id: 'full-width', name: 'Full Width',   icon: '📄', description: 'Full-width single post/article layout' },
    { id: 'sidebar',    name: 'With Sidebar', icon: '📑', description: 'Post with related-content sidebar' }
  ],
  '404': [
    { id: 'minimal',    name: 'Minimal',         icon: '🚧', description: 'Clean 404 page with home link' },
    { id: 'search',     name: 'With Search',     icon: '🔍', description: '404 page with search box' },
    { id: 'navigation', name: 'With Navigation', icon: '🧭', description: 'Quick links to popular pages' }
  ],
  search: [
    { id: 'standard', name: 'Standard Results', icon: '📋', description: 'Clean search results list' },
    { id: 'filtered',  name: 'With Filters',    icon: '🔽', description: 'Results list with filter sidebar' }
  ]
};

// ---------------------------------------------------------------------------
// LayoutPresetPicker — compact card picker for per-template layouts
// ---------------------------------------------------------------------------

const LayoutPresetPicker = ({ presets, value, onChange }) => (
  <Grid container spacing={1.5}>
    {presets.map((preset) => {
      const isSelected = value === preset.id;
      return (
        <Grid item xs={12} sm={6} md={3} key={preset.id}>
          <Card
            variant="outlined"
            sx={{
              borderColor: isSelected ? 'primary.main' : 'divider',
              borderWidth: isSelected ? 2 : 1,
              bgcolor: isSelected ? 'primary.50' : 'background.paper',
              transition: 'border-color 0.15s, background-color 0.15s',
              height: '100%'
            }}
          >
            <CardActionArea onClick={() => onChange(preset.id)} sx={{ height: '100%' }}>
              <CardContent sx={{ p: 1.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                  <Typography variant="h6" component="span" sx={{ lineHeight: 1 }}>
                    {preset.icon}
                  </Typography>
                  {isSelected
                    ? <CheckCircleIcon color="primary" fontSize="small" />
                    : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                  }
                </Box>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  {preset.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {preset.description}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      );
    })}
  </Grid>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const TemplateCustomizer = ({
  selectedTemplates = [],
  industry = null,
  customizations = {},
  onCustomizationChange
}) => {
  // Track which accordion panel is expanded (only one at a time for clarity)
  const [expanded, setExpanded] = useState(selectedTemplates[0] || false);

  const handleAccordionChange = (templateId) => (_, isExpanded) => {
    setExpanded(isExpanded ? templateId : false);
  };

  const handleLayoutPresetChange = (templateId, layoutPreset) => {
    const current = customizations[templateId] || {};
    onCustomizationChange(templateId, { ...current, layoutPreset });
  };

  const handleSectionsChange = (templateId, sections) => {
    const current = customizations[templateId] || {};
    onCustomizationChange(templateId, { ...current, sections });
  };

  /**
   * A template is "customizable" when it has a known entry in TEMPLATE_LAYOUT_PRESETS.
   *
   * Templates absent from that map are auto-styled by the theme and not shown here.
   */
  const isCustomizable = (templateId) => templateId in TEMPLATE_LAYOUT_PRESETS;

  const customizableTemplates = selectedTemplates.filter(isCustomizable);

  if (customizableTemplates.length === 0) {
    return (
      <Alert severity="warning">
        No customizable templates selected. Go back to Step 4 and choose at least one template.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Customise Your Templates
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Fine-tune the layout and content sections for each template in your theme. Enabled
        sections will be generated in the order shown.
      </Typography>

      {industry && (
        <Alert severity="info" icon={false} sx={{ mb: 2 }}>
          <Typography variant="body2">
            🏷️ Industry-specific sections (e.g. mortgage calculator, agent directory) are
            available where relevant to your selected industry.
          </Typography>
        </Alert>
      )}

      {/* ── Customizable templates ───────────────────────────────── */}
      <>
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <Typography variant="subtitle1" fontWeight="bold">
            Core Templates — Fully Customizable
          </Typography>
        </Box>

        {customizableTemplates.map((templateId) => {
            const meta = TEMPLATE_META[templateId] || { name: templateId, icon: '📄', note: null };
            const config = customizations[templateId] || { layoutPreset: null, sections: [] };
            const presets = TEMPLATE_LAYOUT_PRESETS[templateId]; // null for home
            const enabledSections = (config.sections || []).filter((s) => s.enabled).length;
            const totalSections = (config.sections || []).length;

            return (
              <Accordion
                key={templateId}
                expanded={expanded === templateId}
                onChange={handleAccordionChange(templateId)}
                variant="outlined"
                sx={{ mb: 1, '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={1.5} width="100%">
                    <Typography variant="h6" component="span" sx={{ lineHeight: 1 }}>
                      {meta.icon}
                    </Typography>
                    <Box flex={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {meta.name}
                      </Typography>
                      {meta.note && (
                        <Typography variant="caption" color="text.secondary">
                          {meta.note}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" gap={0.75} alignItems="center" mr={1}>
                      {totalSections > 0 && (
                        <Chip
                          label={`${enabledSections}/${totalSections} sections`}
                          size="small"
                          color={enabledSections > 0 ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      )}
                      {config.layoutPreset && presets && (
                        <Chip
                          label={presets.find((p) => p.id === config.layoutPreset)?.name || config.layoutPreset}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  {/* Layout preset picker — hidden for homepage (uses global LayoutPicker) */}
                  {presets && presets.length > 0 && (
                    <Box mb={3}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Layout Preset
                      </Typography>
                      <LayoutPresetPicker
                        presets={presets}
                        value={config.layoutPreset}
                        onChange={(val) => handleLayoutPresetChange(templateId, val)}
                      />
                      <Divider sx={{ mt: 2.5 }} />
                    </Box>
                  )}

                  {/* Section manager */}
                  {config.sections && config.sections.length > 0 ? (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: presets ? 2 : 0 }}>
                        Content Sections
                      </Typography>
                      <SectionManager
                        templateId={templateId}
                        industry={industry}
                        sections={config.sections}
                        onChange={(updatedSections) => handleSectionsChange(templateId, updatedSections)}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {presets
                        ? 'Select a layout preset above, then return here to adjust sections.'
                        : 'No configurable sections for this template.'}
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </>
    </Box>
  );
};

export { TEMPLATE_LAYOUT_PRESETS };
export default TemplateCustomizer;
